import mergeWith from 'lodash-es/mergeWith';
import { isAbsoluteUrl } from 'ha/utils/isAbsoluteUrl';
import type { Logger } from '@hbf/log';

export class FetchClient {
  private baseUrl: string;

  private options: RequestInit;

  private authorizationHeader?: () => string | undefined;

  private logger?: Logger;

  private timeout?: number;

  constructor(
    url: string,
    options?: RequestInit,
    authorizationHeader?: () => string | undefined,
    timeout?: number,
    logger?: Logger,
  ) {
    this.baseUrl = url;
    this.options = { credentials: 'include', ...options };
    this.authorizationHeader = authorizationHeader;
    this.timeout = timeout;
    this.logger = logger;
  }

  fetch(url: string, options?: RequestInit) {
    const mergedOptions = mergeWith(
      {},
      this.options,
      options || {},
      (baseOption, newOption) =>
        Array.isArray(baseOption) && Array.isArray(newOption)
          ? newOption
          : undefined,
    );

    if (this.authorizationHeader) {
      const authHeader = this.authorizationHeader();

      if (authHeader) {
        mergedOptions.headers = {
          ...mergedOptions.headers,
          Authorization: authHeader,
        };
      }
    }

    let timeoutId: NodeJS.Timeout | undefined;

    if (this.timeout) {
      const controller = new AbortController();
      const { signal } = controller;

      mergedOptions.signal = signal;

      timeoutId = globalThis.setTimeout(() => {
        controller.abort();
      }, this.timeout);
    }

    return fetch(
      isAbsoluteUrl(url) ? url : `${this.baseUrl}${url}`,
      mergedOptions,
    )
      .then(response => {
        if (timeoutId) {
          globalThis.clearTimeout(timeoutId);
        }
        return response;
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          this.logger?.debug({
            message: `request aborted`,
            url,
            options,
            error,
          });
        }
        throw error;
      });
  }
}
